import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { TemplateDto, TemplateUpdateRequestDto } from "@/dto/TemplatesDto";
import { Yn } from "@/constants/code";
import { useEffect } from "react";

/**
 * 템플릿 수정 폼 Hook
 *
 * @param templateInfo 수정할 템플릿 정보
 * @param onSubmit 폼 제출 핸들러
 * @returns
 *
 * @author Max <sud665@ymjlab.com>
 * @version 1.0
 * @since 2025-05-09
 */
export default function useEditForm(
  templateInfo: TemplateDto,
  onSubmit: (data: FormData, id: string) => void
) {
  const methods = useForm<TemplateUpdateRequestDto>({
    resolver: yupResolver(
      yup.object().shape({
        templateId: yup.string().required("템플릿 ID를 입력해주세요."),
        productId: yup.string().required("상품 ID를 선택해주세요."),
        templateNm: yup.string().required("템플릿명을 입력해주세요."),
        templateUrl: yup
          .string()
          .required("템플릿 URL을 입력해주세요.")
          .url("올바른 URL 형식을 입력해주세요."),
        templateDtl: yup.string().required("템플릿 상세 설명을 입력해주세요."),
        dispYn: yup.mixed<Yn>().oneOf([Yn.Y, Yn.N]).required(),
        dispSn: yup
          .number()
          .required("표시 순서를 입력해주세요.")
          .transform((value) => (isNaN(value) ? undefined : value))
          .min(1, "1 이상의 숫자를 입력해주세요."),
        file: yup.mixed<File>().optional(),
      })
    ),
    defaultValues: {
      templateId: "",
      productId: "",
      templateNm: "",
      templateUrl: "",
      templateDtl: "",
      dispYn: Yn.Y,
      dispSn: 1,
      file: undefined,
    },
  });

  // 템플릿 정보가 변경될 때마다 폼 값을 업데이트
  useEffect(() => {
    if (templateInfo) {
      methods.reset({
        templateId: templateInfo.templateId,
        productId: templateInfo.productId,
        templateNm: templateInfo.templateNm,
        templateUrl: templateInfo.templateUrl,
        templateDtl: templateInfo.templateDtl,
        dispYn: templateInfo.dispYn,
        dispSn: templateInfo.dispSn,
      });
    }
  }, [templateInfo, methods]);

  /**
   * 폼 제출 핸들러
   *
   * @param data 폼 데이터
   * @param id 템플릿 ID
   */
  const handleSubmit = methods.handleSubmit((data) => {
    // formData 생성
    const formData = new FormData();

    // 기본 정보 추가
    formData.append("productId", data.productId || "");
    formData.append("templateNm", data.templateNm || "");
    formData.append("templateUrl", data.templateUrl || "");
    formData.append("templateDtl", data.templateDtl || "");
    formData.append("dispYn", data.dispYn || Yn.Y);

    // dispSn는 넘버 타입이므로 문자열로 변환
    formData.append("dispSn", data.dispSn?.toString() || "1");

    // 파일이 있는 경우에만 추가
    if (data.file) {
      formData.append("file", data.file);
    }

    // 템플릿 ID와 함께 제출
    onSubmit(formData, templateInfo.templateId);
  });

  return {
    methods,
    handleSubmit,
  };
}
